import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useQueryState } from '@/hooks/state/useQueryState';
import {
  buildFreeMusicQuery,
  buildPremiumMusicQuery,
  deduplicateSearchHits,
} from '@music-vine/setlist';
import { searchKeys } from '@/utils/queryKeys';
import { setlist } from '@/utils/typesense';

import type {
  MusicDocument,
  SearchResponse,
  SearchResponseHit,
  SortOption,
} from '@music-vine/setlist';
import type { SortScoreVersion } from '@/atoms/typesenseDebug';

interface UseFreeFirstMusicSearchArgs {
  // The page's membership filter; required with `contextSlug`.
  contextFilter?: string;
  contextPath: string;
  // `false` on global /music/all; the page's public path on collection/tag
  // routes, so split sections hit the same engagement-sorted collection.
  contextSlug: string | false;
  filters: string;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}

export interface FreeFirstTierResult {
  hasMore: boolean;
  hits: SearchResponseHit<MusicDocument>[];
  isFetchingMore: boolean;
  onShowMore: () => void;
  // Pages loaded for this tier — the `pageIndex` analytics dimension.
  pageCount: number;
}

const FREE_TIER_PER_PAGE = 25;
// Premium teaser: 5 first, then 25 per "Show more" (offset-based to stay contiguous).
const PREMIUM_TEASER_SIZE = 5;
const PREMIUM_PAGE_SIZE = 25;

// 5 minutes — matches the single-stream search query's cache lifetime.
const STALE_TIME_MS = 300_000;

const trackKey = (document: MusicDocument) => document.track_id;

type TierPage = {
  expectedSize: number;
  hits: SearchResponseHit<MusicDocument>[];
  page: number;
};

// Premium paginates by offset with a small first page, so page 1 shows the
// 5-track teaser and each later "Show more" appends 25 contiguous tracks.
const premiumPageWindow = (page: number) =>
  page === 1
    ? { perPage: PREMIUM_TEASER_SIZE, offset: 0 }
    : {
        perPage: PREMIUM_PAGE_SIZE,
        offset: PREMIUM_TEASER_SIZE + (page - 2) * PREMIUM_PAGE_SIZE,
      };

// Each tier is its own `is_premium`-filtered search: the split lives in the
// query filter, not in slicing a combined response, so it stays correct on
// every sort (the earlier combined-query approach leaked free tracks into
// premium on non-relevancy sorts). Not SSR-prefetched — fetches on mount.
export const useFreeFirstMusicSearch = ({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  contextSlug,
  contextFilter,
}: UseFreeFirstMusicSearchArgs) => {
  // Popular/Alternative pill preference, shared with the single-stream listing.
  const [isAlternativeFirst] = useQueryState('altSort', {
    defaultValue: false,
    parse: (value) => value === 'true',
    serialize: (value) => (value ? 'true' : ''),
  });

  const buildTierSearch = (tier: 'free' | 'premium', page: number) => {
    if (tier === 'premium') {
      const { perPage, offset } = premiumPageWindow(page);
      // Drop the builder's `page` — Typesense ignores `offset` when `page` is set.
      const { page: _page, ...search } = buildPremiumMusicQuery({
        q: query,
        filter_by: filters,
        sort,
        page,
        sortScoreVersion,
        contextSlug,
        contextFilter,
        isAlternativeFirst,
        perPage: perPage * 2,
      });
      return {
        search: { ...search, offset, per_page: perPage },
        expectedSize: perPage,
      };
    }

    return {
      search: buildFreeMusicQuery({
        q: query,
        filter_by: filters,
        sort,
        page,
        sortScoreVersion,
        contextSlug,
        contextFilter,
        isAlternativeFirst,
        perPage: FREE_TIER_PER_PAGE * 2,
      }),
      expectedSize: FREE_TIER_PER_PAGE,
    };
  };

  const tierQueryOptions = (tier: 'free' | 'premium') => ({
    queryKey: searchKeys.musicTier({
      tier,
      query,
      filters,
      sort,
      sortScoreVersion,
      contextPath,
      contextSlug,
      isAlternativeFirst,
    }),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: number;
    }): Promise<TierPage> => {
      const { search, expectedSize } = buildTierSearch(tier, pageParam);
      const { results } = (await setlist.multiSearch(
        { searches: [search] },
        { use_cache: true }
      )) as { results: SearchResponse<MusicDocument>[] };

      return { hits: results[0]?.hits ?? [], page: pageParam, expectedSize };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: TierPage) =>
      lastPage.hits.length >= lastPage.expectedSize
        ? lastPage.page + 1
        : undefined,
    staleTime: STALE_TIME_MS,
  });

  const freeTier = useInfiniteQuery(tierQueryOptions('free'));
  const premiumTier = useInfiniteQuery(tierQueryOptions('premium'));

  const free = useMemo<FreeFirstTierResult>(() => {
    const hits = deduplicateSearchHits(
      (freeTier.data?.pages ?? []).flatMap((p) => p.hits),
      trackKey
    );

    return {
      hits,
      hasMore: !!freeTier.hasNextPage,
      isFetchingMore: freeTier.isFetchingNextPage,
      onShowMore: freeTier.fetchNextPage,
      pageCount: freeTier.data?.pages.length ?? 0,
    };
  }, [
    freeTier.data,
    freeTier.hasNextPage,
    freeTier.isFetchingNextPage,
    freeTier.fetchNextPage,
  ]);

  const premium = useMemo<FreeFirstTierResult>(() => {
    const hits = deduplicateSearchHits(
      (premiumTier.data?.pages ?? []).flatMap((p) => p.hits),
      trackKey
    );

    return {
      hits,
      hasMore: !!premiumTier.hasNextPage,
      isFetchingMore: premiumTier.isFetchingNextPage,
      onShowMore: premiumTier.fetchNextPage,
      pageCount: premiumTier.data?.pages.length ?? 0,
    };
  }, [
    premiumTier.data,
    premiumTier.hasNextPage,
    premiumTier.isFetchingNextPage,
    premiumTier.fetchNextPage,
  ]);

  return {
    free,
    premium,
    isLoading: freeTier.isLoading || premiumTier.isLoading,
    isError: freeTier.isError || premiumTier.isError,
  };
};
